import { useEffect, useState, useRef } from 'react';
import logger from 'utils/logger';

export const FETCH_STATUS = {
  STARTED: 'started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
};

const log = logger.createLogger('useAIAnalytics');

/**
 * Custom hook for managing AI analytics data and operations
 * Extracts the data fetching and management logic from the component
 */
export function useAIAnalytics({
  analyticData,
  fetchData,
  fetchStatus,
  rerunDataAnalysis,
}) {
  const [attachments, setAttachments] = useState(
    analyticData.data ? analyticData.data.attachmentsAnalytics : []
  );
  const [startingId, setStartingId] = useState(null);

  // Track if already called fetchData; prevent StrictMode double calls
  const hasCalledFetchData = useRef(false);
  const lastFetchStatusRef = useRef(null);

  useEffect(() => {
    let intervalId;

    if (analyticData.fetchStatus === FETCH_STATUS.STARTED) {
      // Only call fetchData if we haven't already called it for this STARTED status
      if (
        !hasCalledFetchData.current ||
        lastFetchStatusRef.current !== FETCH_STATUS.STARTED
      ) {
        hasCalledFetchData.current = true;
        lastFetchStatusRef.current = FETCH_STATUS.STARTED;
        fetchData();
      } else {
        log.debug(
          '⚠️ BLOCKED fetchData() - Already called for this STARTED status'
        );
      }
    } else if (analyticData.fetchStatus === FETCH_STATUS.IN_PROGRESS) {
      intervalId = setInterval(() => {
        fetchStatus();
      }, 3000);
    } else if (analyticData.fetchStatus === FETCH_STATUS.COMPLETED) {
      // Reset the flag for a new run
      hasCalledFetchData.current = false;
      lastFetchStatusRef.current = FETCH_STATUS.COMPLETED;

      if (analyticData.data?.attachmentsAnalytics) {
        setStartingId(
          Math.min(
            ...analyticData.data.attachmentsAnalytics.map((item) => item.id)
          ) - 1
        );
        setAttachments(analyticData.data.attachmentsAnalytics);
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [analyticData, fetchData, fetchStatus]);

  return {
    attachments,
    startingId,
    isLoading: analyticData.fetchStatus !== FETCH_STATUS.COMPLETED,
    isInProgress: analyticData.fetchStatus === FETCH_STATUS.IN_PROGRESS,
    rerunAnalysis: rerunDataAnalysis,
    fetchStatus: analyticData.fetchStatus,
  };
}
